import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Search, Mail, Hash, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMENSION_COLORS = {
  reliability: { bar: 'bg-blue-500', label: 'Reliability' },
  quality: { bar: 'bg-emerald-500', label: 'Quality' },
  collaboration: { bar: 'bg-purple-500', label: 'Collaboration' },
  professionalism: { bar: 'bg-amber-500', label: 'Professionalism' },
  discipline: { bar: 'bg-red-500', label: 'Discipline' },
};

const CheckScore = () => {
  const [ainaaId, setAinaaId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!ainaaId.trim() || !email.trim()) {
      toast.error('Both fields are required');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/check-score?ainaa_id=${encodeURIComponent(ainaaId.trim())}&email=${encodeURIComponent(email.trim())}`);
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not retrieve score';
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const getScoreColor = (s) => s >= 800 ? 'text-emerald-700' : s >= 600 ? 'text-blue-700' : s >= 400 ? 'text-amber-700' : 'text-red-700';
  const getScoreBg = (s) => s >= 800 ? 'border-emerald-300 bg-emerald-50' : s >= 600 ? 'border-blue-300 bg-blue-50' : s >= 400 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50';

  return (
    <div className="min-h-screen bg-[#F5F3EE]">
      <Navigation />
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-10">
              <Shield className="w-12 h-12 text-[#0F1B2D] mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0F1B2D] mb-2">Check Trust Score</h1>
              <p className="text-sm text-[#9B9589] max-w-md mx-auto">Enter any AINAA member's ID and your registered email to view their Trust Score.</p>
            </div>

            <form onSubmit={handleCheck} className="border-2 border-[#0F1B2D] bg-white p-8 mb-8" data-testid="check-score-form">
              <div className="space-y-5">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-2">AINAA ID *</label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D3CB]" size={16} />
                    <Input value={ainaaId} onChange={(e) => setAinaaId(e.target.value.toUpperCase())}
                      placeholder="AINAA-PPL-XXXXX" className="input-ainaa h-12 pl-10 uppercase font-mono" required data-testid="check-ainaa-id" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-2">Your Registered Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D3CB]" size={16} />
                    <Input value={email} onChange={(e) => setEmail(e.target.value)}
                      type="email" placeholder="your@email.com" className="input-ainaa h-12 pl-10" required data-testid="check-email" />
                  </div>
                  <p className="text-[10px] text-[#9B9589] mt-1">Only registered AINAA members can check trust scores. This action is logged.</p>
                </div>
                <button type="submit" disabled={loading} className="btn-ainaa-primary w-full py-3 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2" data-testid="check-score-btn">
                  {loading ? 'Checking...' : <><Search size={16} />Check Score</>}
                </button>
              </div>
            </form>

            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-red-300 bg-red-50 p-5 mb-6 flex items-start gap-3" data-testid="check-score-error">
                <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </motion.div>
            )}

            {result && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`border-2 ${getScoreBg(result.trust_score?.overall_score || 0)} p-8`} data-testid="check-score-result">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#0F1B2D]">{result.name}</h2>
                    <span className="text-[11px] font-mono text-[#C4A35A] font-bold">{result.ainaa_id}</span>
                    <span className="block text-xs text-[#9B9589] mt-0.5 capitalize">{result.profession}</span>
                  </div>
                  <div className="text-right">
                    <p className={`font-['Playfair_Display'] text-5xl font-bold ${getScoreColor(result.trust_score?.overall_score || 0)}`} data-testid="score-value">
                      {result.trust_score?.overall_score || 0}
                    </p>
                    <p className="text-[10px] text-[#9B9589] uppercase tracking-wider">/ 1000</p>
                  </div>
                </div>

                {result.trust_score?.dimensions && Object.keys(result.trust_score.dimensions).length > 0 && (
                  <div className="space-y-3 mb-5">
                    {Object.entries(result.trust_score.dimensions).map(([key, value]) => {
                      const cfg = DIMENSION_COLORS[key] || DIMENSION_COLORS.reliability;
                      return (
                        <div key={key}>
                          <div className="flex justify-between mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#4A4640]">{cfg.label}</span>
                            <span className="text-[10px] font-bold text-[#9B9589]">{value}/200</span>
                          </div>
                          <div className="h-2 bg-white/60 w-full">
                            <div className={`h-full ${cfg.bar} transition-all duration-700`} style={{ width: `${Math.min((value / 200) * 100, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {result.trust_score?.last_calculated && (
                  <div className="flex items-center gap-1.5 text-[10px] text-[#9B9589]">
                    <Clock size={10} /> Last calculated: {new Date(result.trust_score.last_calculated).toLocaleDateString()}
                  </div>
                )}
              </motion.div>
            )}

            <div className="mt-6 border border-[#D8D3CB] bg-[#EDECEA] p-4">
              <p className="text-[10px] text-[#9B9589] leading-relaxed">
                Trust Scores are recalculated every 20–25 days. The score reflects a member's reliability, work quality, collaboration, professionalism, and discipline — as recorded across AINAA, Slate, and Lobby. Each lookup is logged for transparency.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckScore;
